import { LockOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch, useTitle, useTranslation } from "hooks";
import { PublicLayout } from "layouts";
import AuthPageLayout from "pages/auth/AuthPageLayout";
import { authFooterLinkSx, authStackSpacing, fieldSx, iconSx, primaryBtnSx } from "pages/auth/formShared";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { Link as LinkRouter, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { validatePassword } from "utils";

export default function Password() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const otp: string | undefined = useLocation().state?.otp;
  const [email, isSetup] = useMemo(() => {
    return [searchParams.get("email"), searchParams.get("setup") === "true"];
  }, [searchParams]);
  const [state, setState] = useState({
    email: email || "",
    otp: typeof otp === "string" ? Number.parseInt(otp, 10) : 0,
    newPassword: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({
    email: email ? "" : "Invalid Email",
    otp: otp ? "" : "Invalid OTP",
    newPassword: "",
    confirmPassword: ""
  });
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchPut } = useFetch<typeof state>("PUT");

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPut(ENDPOINTS.forget, {
        body: JSON.stringify({
          email: state.email,
          otp: state.otp,
          newPassword: state.newPassword
        })
      }).catch(console.error);
    },
    [state, callFetchPut]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response?.message || "", { variant: "success" });
      navigate("/login", { replace: true });
    }
  }, [response, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error?.message || JSON.stringify(error), { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  useTitle(isSetup ? "Set Up Password" : "Forgot Password");

  const passwordValid = useMemo(
    () =>
      Boolean(
        state.newPassword &&
          state.confirmPassword &&
          !errors.newPassword &&
          !errors.confirmPassword &&
          state.newPassword === state.confirmPassword
      ),
    [state, errors]
  );

  return (
    <PublicLayout>
      <AuthPageLayout
        title={isSetup ? t["Set Up Password"] : t["Forgot Password"]}
        subtitle={isSetup ? t["Create password to access account"] : t["Enter new password"]}
        footer={
          <Link component={LinkRouter} to="/login" sx={authFooterLinkSx}>
            ← {t["Back to sign in"]}
          </Link>
        }
      >
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={authStackSpacing}>
            <input hidden readOnly value={state.email} autoComplete="username" />
            <Box>
              <TextInput
                autoFocus
                required
                fullWidth
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                label={isSetup ? t["Password"] : t["New Password"]}
                helperText={errors?.newPassword}
                error={!!errors?.newPassword}
                startAdornment={<LockOutlined sx={iconSx} />}
                sx={fieldSx}
                onChange={(e) => {
                  setState((prevState) => ({ ...prevState, newPassword: e.target.value }));
                  setErrors((prevState) => ({
                    ...prevState,
                    newPassword: validatePassword(e.target.value)
                  }));
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: COLORS.surface.secondaryText, display: "block", mt: 0.75, lineHeight: 1.6 }}
              >
                {t["Password requirements"]}
              </Typography>
            </Box>
            <TextInput
              required
              fullWidth
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              label={t["Confirm New Password"]}
              helperText={errors?.confirmPassword}
              error={!!errors?.confirmPassword}
              startAdornment={<LockOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prevState) => ({ ...prevState, confirmPassword: e.target.value }));
                setErrors((prevState) => ({
                  ...prevState,
                  confirmPassword: e.target.value !== state.newPassword ? t["Passwords do not match"] : ""
                }));
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!passwordValid || isLoading}
              endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              fullWidth
              sx={primaryBtnSx}
            >
              {t["Submit"]}
            </Button>
          </Stack>
        </Box>
      </AuthPageLayout>
    </PublicLayout>
  );
}
